import React from 'react';
import styled from 'styled-components';
import AnimatedBackground from '../common/AnimatedBackground';

const withAlpha = (color: string, alpha: number) => {
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const normalized = hex.length === 3
      ? hex.split('').map(ch => `${ch}${ch}`).join('')
      : hex;
    const bigint = parseInt(normalized, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  if (color.startsWith('rgb')) {
    const values = color
      .replace('rgba', '')
      .replace('rgb', '')
      .replace('(', '')
      .replace(')', '')
      .split(',')
      .map(v => v.trim());

    const [r = '0', g = '0', b = '0'] = values;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return color;
};

const Container = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.name === 'shadowGrid'
      ? 'transparent'
      : theme.gradients.background || theme.colors.background};
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 4;
  opacity: ${({ theme }) => theme.profile.hud.dotsOpacity};
  background-image: radial-gradient(#ffffff 1px, transparent 1px);
  background-size: 10px 10px;
`;

const HudGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.2;
  pointer-events: none;
  z-index: 3;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 700px;
    height: 700px;
    border: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.25)};
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 500px;
    height: 500px;
    border: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.18)};
    border-radius: 50%;
  }
`;

const ColorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => `radial-gradient(circle at 50% 20%, ${withAlpha(theme.colors.primary, 0.2)}, rgba(0, 0, 0, 0.85)),
              linear-gradient(180deg, ${withAlpha(theme.colors.secondary, 0.18)} 0%, rgba(0, 0, 0, 0.85) 60%)`};
  mix-blend-mode: screen;
  pointer-events: none;
  opacity: ${({ theme }) => theme.profile.hud.overlayOpacity};
  z-index: 1;
`;

const AnimatedGrid = styled(AnimatedBackground)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: ${({ theme }) => (theme.profile.hud.showAnimatedGrid ? 0.85 : 0)};
  transition: opacity 0.6s ease;
  z-index: 2;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 10;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

interface HudBackgroundProps {
  children: React.ReactNode;
}

const HudBackground: React.FC<HudBackgroundProps> = ({ children }) => {
  return (
    <Container>
      <ColorOverlay />
      <AnimatedGrid />
      <BackgroundPattern />
      <HudGrid />
      <ContentWrapper>
        {children}
      </ContentWrapper>
    </Container>
  );
};

export default HudBackground; 
